import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Tabs, Tab, Button, Chip, Grid, Card, CardContent,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
  LinearProgress, Alert, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, IconButton, Tooltip, Avatar, Divider, FormControlLabel, Switch, List, ListItem, ListItemText, ListItemAvatar,
} from '@mui/material';
import { Gavel, Add, Edit, Close, CheckCircle, LockOpen, Visibility } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

const API = process.env.REACT_APP_API_URL || 'http://localhost:4000';
function apiFetch(path: string, opts: RequestInit = {}) {
  return fetch(`${API}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}`, ...(opts.headers || {}) },
  }).then(async r => { const d = await r.json().catch(() => ({})); if (!r.ok) throw new Error(d.message || 'Error'); return d; });
}

const STATUS_CFG: Record<string, { color: any; label: string }> = {
  open:         { color: 'error',   label: '🔴 Open' },
  under_review: { color: 'warning', label: '🔍 Under Review' },
  resolved:     { color: 'success', label: '✅ Resolved' },
  closed:       { color: 'default', label: '🔒 Closed' },
  appealed:     { color: 'primary', label: '⚖️ Appealed' },
};

const TYPE_CFG: Record<string, { label: string; color: string }> = {
  verbal_warning:  { label: 'Verbal Warning',  color: '#f57c00' },
  written_warning: { label: 'Written Warning', color: '#d84315' },
  final_warning:   { label: 'Final Warning',   color: '#b71c1c' },
  pip:             { label: 'PIP',             color: '#4a148c' },
  suspension:      { label: 'Suspension',      color: '#880e4f' },
  termination:     { label: 'Termination',     color: '#1a1a1a' },
  misconduct:      { label: 'Misconduct',      color: '#c62828' },
  grievance:       { label: 'Grievance',       color: '#1565c0' },
  investigation:   { label: 'Investigation',  color: '#37474f' },
};

const SEV_CFG: Record<string, { color: any }> = {
  low:      { color: 'success' },
  medium:   { color: 'warning' },
  high:     { color: 'error' },
  critical: { color: 'error' },
};

interface Case { id: string; caseNumber?: string; employeeId: string; handledById?: string; type: string; status: string; severity: string; title: string; description?: string; incidentDate?: string; hearingDate?: string; resolvedDate?: string; resolution?: string; employeeResponse?: string; isConfidential?: boolean; employee?: { firstName: string; lastName: string; department?: string }; handledBy?: { firstName: string; lastName: string }; actions?: Action[]; }
interface Action { id: string; caseId: string; authorId?: string; actionType: string; notes: string; isInternal: boolean; createdAt: string; author?: { firstName: string; lastName: string }; }

const emptyForm = (): Partial<Case> => ({
  type: 'written_warning', severity: 'medium', isConfidential: true,
  incidentDate: new Date().toISOString().slice(0, 10),
});

export default function Disciplinary() {
  const { user } = useAuth();
  const isHR = ['hr_admin', 'hr_manager'].includes(user?.role ?? '');

  const [cases, setCases] = useState<Case[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formDialog, setFormDialog] = useState<Partial<Case> | null>(null);
  const [detailCase, setDetailCase] = useState<Case | null>(null);
  const [actions, setActions] = useState<Action[]>([]);
  const [noteText, setNoteText] = useState('');
  const [noteInternal, setNoteInternal] = useState(true);
  const [resolveDialog, setResolveDialog] = useState<{ caseId: string; resolution: string } | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [data, st] = await Promise.all([
        apiFetch(`/api/disciplinary${statusFilter ? `?status=${statusFilter}` : ''}`),
        apiFetch('/api/disciplinary/stats'),
      ]);
      setCases(data || []); setStats(st);
    } catch (e: any) { setError(e.message); }
    setLoading(false);
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (c: Case) => {
    const [full, acts] = await Promise.all([
      apiFetch(`/api/disciplinary/${c.id}`),
      apiFetch(`/api/disciplinary/${c.id}/actions`),
    ]);
    setDetailCase(full); setActions(acts || []);
  };

  const save = async () => {
    if (!formDialog) return;
    try {
      formDialog.id
        ? await apiFetch(`/api/disciplinary/${formDialog.id}`, { method: 'PATCH', body: JSON.stringify(formDialog) })
        : await apiFetch('/api/disciplinary', { method: 'POST', body: JSON.stringify(formDialog) });
      setFormDialog(null); load();
    } catch (e: any) { setError(e.message); }
  };

  const addNote = async () => {
    if (!detailCase || !noteText.trim()) return;
    try {
      await apiFetch(`/api/disciplinary/${detailCase.id}/actions`, {
        method: 'POST',
        body: JSON.stringify({ actionType: 'note', notes: noteText, isInternal: noteInternal }),
      });
      const acts = await apiFetch(`/api/disciplinary/${detailCase.id}/actions`);
      setActions(acts || []); setNoteText('');
    } catch (e: any) { setError(e.message); }
  };

  const resolve = async () => {
    if (!resolveDialog?.resolution.trim()) return;
    try {
      const updated = await apiFetch(`/api/disciplinary/${resolveDialog.caseId}/resolve`, {
        method: 'PATCH', body: JSON.stringify({ resolution: resolveDialog.resolution }),
      });
      setResolveDialog(null);
      if (detailCase?.id === resolveDialog.caseId) setDetailCase(updated);
      load();
    } catch (e: any) { setError(e.message); }
  };

  const closeCase = async (id: string) => {
    try {
      const updated = await apiFetch(`/api/disciplinary/${id}/close`, { method: 'PATCH' });
      if (detailCase?.id === id) setDetailCase(updated);
      load();
    } catch (e: any) { setError(e.message); }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Gavel sx={{ fontSize: 32, color: '#b71c1c' }} />
          <Box>
            <Typography variant="h5" fontWeight={700}>Disciplinary & HR Cases</Typography>
            <Typography variant="body2" color="text.secondary">Manage formal HR cases, warnings, and investigations</Typography>
          </Box>
        </Box>
        {isHR && (
          <Button variant="contained" startIcon={<Add />} sx={{ bgcolor: '#b71c1c' }} onClick={() => setFormDialog(emptyForm())}>
            Open Case
          </Button>
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Stats */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[
            { label: 'Total Cases', value: stats.total, color: '#37474f' },
            { label: 'Open', value: stats.open, color: '#c62828' },
            { label: 'Under Review', value: stats.underReview, color: '#e65100' },
            { label: 'Resolved', value: stats.resolved, color: '#2e7d32' },
          ].map(s => (
            <Grid key={s.label} size={{ xs: 6, sm: 3 }}>
              <Card sx={{ textAlign: 'center', p: 1 }}>
                <CardContent sx={{ pb: '8px !important' }}>
                  <Typography variant="h4" fontWeight={700} color={s.color}>{s.value}</Typography>
                  <Typography variant="caption" color="text.secondary">{s.label}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Status filter */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        {['', 'open', 'under_review', 'resolved', 'closed', 'appealed'].map(s => (
          <Chip key={s} label={s ? STATUS_CFG[s]?.label : 'All'} size="small"
            onClick={() => setStatusFilter(s)} color={statusFilter === s ? 'primary' : 'default'}
            variant={statusFilter === s ? 'filled' : 'outlined'} />
        ))}
      </Box>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell><b>Case #</b></TableCell>
              <TableCell><b>Employee</b></TableCell>
              <TableCell><b>Type</b></TableCell>
              <TableCell><b>Severity</b></TableCell>
              <TableCell><b>Incident</b></TableCell>
              <TableCell><b>Handler</b></TableCell>
              <TableCell><b>Status</b></TableCell>
              <TableCell><b>Actions</b></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {cases.map(c => (
              <TableRow key={c.id} hover>
                <TableCell><Typography variant="caption" color="text.secondary" fontFamily="monospace">{c.caseNumber ?? c.id.slice(0, 8)}</Typography></TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar sx={{ width: 28, height: 28, bgcolor: '#b71c1c', fontSize: '0.7rem' }}>
                      {c.employee?.firstName?.[0]}{c.employee?.lastName?.[0]}
                    </Avatar>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>{c.employee?.firstName} {c.employee?.lastName}</Typography>
                      <Typography variant="caption" color="text.secondary">{c.employee?.department}</Typography>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip label={TYPE_CFG[c.type]?.label ?? c.type} size="small"
                    sx={{ bgcolor: TYPE_CFG[c.type]?.color ?? '#666', color: '#fff', fontSize: '0.65rem' }} />
                </TableCell>
                <TableCell><Chip label={c.severity} size="small" color={SEV_CFG[c.severity]?.color} /></TableCell>
                <TableCell>{c.incidentDate ? new Date(c.incidentDate + 'T00:00:00').toLocaleDateString('en-HK', { day: 'numeric', month: 'short' }) : '—'}</TableCell>
                <TableCell>{c.handledBy ? `${c.handledBy.firstName} ${c.handledBy.lastName}` : '—'}</TableCell>
                <TableCell><Chip label={STATUS_CFG[c.status]?.label ?? c.status} size="small" color={STATUS_CFG[c.status]?.color} /></TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Tooltip title="View / Add Notes"><IconButton size="small" onClick={() => openDetail(c)}><Visibility fontSize="small" /></IconButton></Tooltip>
                    {isHR && <Tooltip title="Edit"><IconButton size="small" onClick={() => setFormDialog({ ...c })}><Edit fontSize="small" /></IconButton></Tooltip>}
                    {isHR && c.status === 'open' && <Tooltip title="Resolve"><IconButton size="small" color="success" onClick={() => setResolveDialog({ caseId: c.id, resolution: '' })}><CheckCircle fontSize="small" /></IconButton></Tooltip>}
                    {isHR && c.status === 'resolved' && <Tooltip title="Close Case"><IconButton size="small" onClick={() => closeCase(c.id)}><LockOpen fontSize="small" /></IconButton></Tooltip>}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
            {cases.length === 0 && (
              <TableRow><TableCell colSpan={8} sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>No disciplinary cases.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* ── FORM DIALOG ── */}
      <Dialog open={!!formDialog} onClose={() => setFormDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {formDialog?.id ? 'Edit Case' : 'Open New Case'}
          <IconButton sx={{ position: 'absolute', right: 8, top: 8 }} onClick={() => setFormDialog(null)}><Close /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField label="Employee ID" fullWidth required value={formDialog?.employeeId ?? ''}
              onChange={e => setFormDialog(p => ({ ...p, employeeId: e.target.value }))} />
            <TextField label="Case Title" fullWidth required value={formDialog?.title ?? ''}
              onChange={e => setFormDialog(p => ({ ...p, title: e.target.value }))} />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField label="Case Type" select fullWidth value={formDialog?.type ?? 'written_warning'}
                onChange={e => setFormDialog(p => ({ ...p, type: e.target.value }))}>
                {Object.entries(TYPE_CFG).map(([v, c]) => <MenuItem key={v} value={v}>{c.label}</MenuItem>)}
              </TextField>
              <TextField label="Severity" select fullWidth value={formDialog?.severity ?? 'medium'}
                onChange={e => setFormDialog(p => ({ ...p, severity: e.target.value }))}>
                {['low', 'medium', 'high', 'critical'].map(s => <MenuItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</MenuItem>)}
              </TextField>
            </Box>
            <TextField label="Incident Date" type="date" fullWidth InputLabelProps={{ shrink: true }}
              value={formDialog?.incidentDate ?? ''} onChange={e => setFormDialog(p => ({ ...p, incidentDate: e.target.value }))} />
            <TextField label="Description / Allegations" fullWidth multiline rows={3} value={formDialog?.description ?? ''}
              onChange={e => setFormDialog(p => ({ ...p, description: e.target.value }))} />
            <TextField label="Hearing Date (optional)" type="date" fullWidth InputLabelProps={{ shrink: true }}
              value={formDialog?.hearingDate ?? ''} onChange={e => setFormDialog(p => ({ ...p, hearingDate: e.target.value }))} />
            <FormControlLabel control={<Switch checked={formDialog?.isConfidential ?? true}
              onChange={e => setFormDialog(p => ({ ...p, isConfidential: e.target.checked }))} />}
              label="Confidential" />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFormDialog(null)}>Cancel</Button>
          <Button variant="contained" sx={{ bgcolor: '#b71c1c' }} onClick={save}
            disabled={!formDialog?.employeeId || !formDialog?.title}>
            {formDialog?.id ? 'Update' : 'Open Case'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── DETAIL DIALOG ── */}
      <Dialog open={!!detailCase} onClose={() => setDetailCase(null)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Typography variant="caption" fontFamily="monospace" color="text.secondary">{detailCase?.caseNumber}</Typography>
            <Typography fontWeight={700}>{detailCase?.title}</Typography>
            {detailCase && <Chip label={STATUS_CFG[detailCase.status]?.label} size="small" color={STATUS_CFG[detailCase.status]?.color} />}
          </Box>
          <IconButton sx={{ position: 'absolute', right: 8, top: 8 }} onClick={() => setDetailCase(null)}><Close /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {detailCase && (
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 5 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography variant="body2"><b>Employee:</b> {detailCase.employee?.firstName} {detailCase.employee?.lastName}</Typography>
                  <Typography variant="body2"><b>Type:</b> <Chip label={TYPE_CFG[detailCase.type]?.label} size="small" sx={{ bgcolor: TYPE_CFG[detailCase.type]?.color, color: '#fff' }} /></Typography>
                  <Typography variant="body2"><b>Severity:</b> <Chip label={detailCase.severity} size="small" color={SEV_CFG[detailCase.severity]?.color} /></Typography>
                  <Typography variant="body2"><b>Incident Date:</b> {detailCase.incidentDate ?? '—'}</Typography>
                  {detailCase.hearingDate && <Typography variant="body2"><b>Hearing:</b> {detailCase.hearingDate}</Typography>}
                  <Typography variant="body2"><b>Handler:</b> {detailCase.handledBy ? `${detailCase.handledBy.firstName} ${detailCase.handledBy.lastName}` : '—'}</Typography>
                  {detailCase.description && <><Divider /><Typography variant="body2" color="text.secondary">{detailCase.description}</Typography></>}
                  {detailCase.resolution && (
                    <Box sx={{ bgcolor: '#e8f5e9', p: 1.5, borderRadius: 1 }}>
                      <Typography variant="caption" color="text.secondary">Resolution</Typography>
                      <Typography variant="body2">{detailCase.resolution}</Typography>
                    </Box>
                  )}
                  {isHR && detailCase.status === 'open' && (
                    <Button variant="outlined" color="success" size="small"
                      onClick={() => setResolveDialog({ caseId: detailCase.id, resolution: '' })}>
                      Resolve Case
                    </Button>
                  )}
                </Box>
              </Grid>
              <Grid size={{ xs: 12, md: 7 }}>
                <Typography fontWeight={700} sx={{ mb: 1 }}>Case Timeline</Typography>
                <Paper variant="outlined" sx={{ maxHeight: 300, overflow: 'auto', mb: 2 }}>
                  <List dense>
                    {actions.map(a => (
                      <ListItem key={a.id} alignItems="flex-start" sx={{ borderBottom: '1px solid #f0f0f0' }}>
                        <ListItemAvatar>
                          <Avatar sx={{ width: 28, height: 28, bgcolor: a.isInternal ? '#e0e0e0' : '#1a237e', fontSize: '0.65rem' }}>
                            {a.author?.firstName?.[0] ?? 'H'}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={<Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2" fontWeight={600}>{a.author ? `${a.author.firstName} ${a.author.lastName}` : 'HR'}</Typography>
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              {a.isInternal && <Chip label="Internal" size="small" sx={{ fontSize: '0.6rem', height: 16 }} />}
                              <Typography variant="caption" color="text.secondary">{new Date(a.createdAt).toLocaleDateString('en-HK', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</Typography>
                            </Box>
                          </Box>}
                          secondary={a.notes}
                        />
                      </ListItem>
                    ))}
                    {actions.length === 0 && <ListItem><ListItemText primary={<Typography color="text.secondary" variant="body2">No notes yet.</Typography>} /></ListItem>}
                  </List>
                </Paper>
                {isHR && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <TextField label="Add Note" fullWidth multiline rows={2} size="small" value={noteText}
                      onChange={e => setNoteText(e.target.value)} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <FormControlLabel control={<Switch size="small" checked={noteInternal} onChange={e => setNoteInternal(e.target.checked)} />}
                        label={<Typography variant="caption">Internal only</Typography>} />
                      <Button variant="contained" size="small" onClick={addNote} disabled={!noteText.trim()}>Add Note</Button>
                    </Box>
                  </Box>
                )}
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          {isHR && detailCase?.status === 'resolved' && (
            <Button onClick={() => closeCase(detailCase!.id)}>Close Case</Button>
          )}
          <Button onClick={() => setDetailCase(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* ── RESOLVE DIALOG ── */}
      <Dialog open={!!resolveDialog} onClose={() => setResolveDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Resolve Case</DialogTitle>
        <DialogContent dividers>
          <TextField label="Resolution Summary *" fullWidth multiline rows={3}
            value={resolveDialog?.resolution ?? ''}
            onChange={e => setResolveDialog(p => p ? { ...p, resolution: e.target.value } : null)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResolveDialog(null)}>Cancel</Button>
          <Button variant="contained" color="success" onClick={resolve} disabled={!resolveDialog?.resolution?.trim()}>Mark Resolved</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
